import * as React from "react"
import { OAuthService } from "@digits-graphql/frontend/graphql-bearer"
import { type ReadInvitationRecipientQuery } from "@digits-graphql/frontend/graphql-public"
import { LoggedRedirect } from "@digits-shared/components/Router/LoggedRedirect"
import { SvgMail01 } from "@digits-shared/components/SVGIcons/line/Mail01.svg"
import { OrText } from "@digits-shared/components/UI/Elements/OrDivider"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import { Input } from "@digits-shared/DesignSystem/FormElements/Input"
import { EMAIL_ADDRESS_PATTERN_REGEX } from "@digits-shared/helpers/emailAddressPattern"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { useLocationRedirect } from "src/frontend/components/Public/hooks/useLocationRedirect"
import { useOAuthSignIn } from "src/frontend/components/Public/hooks/useOAuthSignIn"
import { useReadInvitationRecipient } from "src/frontend/components/Public/hooks/useReadInvitationRecipient"
import { useSignIn } from "src/frontend/components/Public/hooks/useSignIn"
import { useOnboardingContext } from "src/frontend/components/Public/Onboarding/OnboardingContext"
import { EmailSignInField } from "src/frontend/components/Public/Onboarding/reducer"
import { Title } from "src/frontend/components/Shared/Glow/Header"
import type FrontendSession from "src/frontend/session"
import { ContinueWithGoogle } from "src/shared/components/Session/ContinueWithGoogle"
import { GoogleLogin } from "src/shared/components/Session/GoogleLogIn"

/*
  STYLES
*/

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledOrText = styled(OrText)`
  align-self: center;
  opacity: 1;
  font-weight: ${fonts.weight.heavy};
  font-size: 12px;
  color: ${colors.secondary60};
`

/*
  COMPONENTS
*/

export const SignIn: React.FC = () => {
  const session = useSession<FrontendSession>()
  const {
    dispatch,
    isFieldInvalid,
    fieldHasValidValue,
    onFieldChanged,
    onFieldBlurred,
    getFieldValue,
    state: { blockLoginRedirect, loading },
  } = useOnboardingContext()
  const { recipientData } = useReadInvitationRecipient()
  const { sessionRootUrl } = useLocationRedirect()
  const { loggedInUserEmail, submittedOnce } = useSignIn()
  const {
    onSuccess: onGoogleSuccess,
    onFailure: onGoogleFailure,
    isLoading: isGoogleLoading,
    setLoading: setGoogleLoading,
    unsetLoading: unsetGoogleLoading,
  } = useOAuthSignIn(OAuthService.Google)

  const showEmailSignIn = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()

      const emailAddress = getFieldValue(EmailSignInField.EmailAddress)
      if (!emailAddress) return

      dispatch({ type: "SHOW_EMAIL_SIGN_IN", emailAddress })
    },
    [dispatch, getFieldValue]
  )

  const isEmailValid = fieldHasValidValue(EmailSignInField.EmailAddress)
  const isEmailInvalid = isFieldInvalid(EmailSignInField.EmailAddress)
  const email = getFieldValue(EmailSignInField.EmailAddress)

  const identityMismatch = (userEmail?: string, data?: ReadInvitationRecipientQuery) =>
    !!userEmail && data && data.readInvitationRecipient.emailAddress !== loggedInUserEmail

  if (
    session.hasUserData &&
    !identityMismatch(loggedInUserEmail, recipientData) &&
    // Don't attempt redirect for having data if we have submitted.
    // This prevents an early redirect when logging in.
    !submittedOnce.current &&
    !blockLoginRedirect
  ) {
    return <LoggedRedirect name="PublicLogIn-alreadyLoggedIn" to={sessionRootUrl} />
  }

  const isAuthing = isGoogleLoading || loading

  return (
    <>
      <Title>Sign in to Digits</Title>
      <Form onSubmit={showEmailSignIn}>
        <Input
          autoFocus
          id={EmailSignInField.EmailAddress}
          name={EmailSignInField.EmailAddress}
          placeholder="Enter email"
          label="Enter email"
          hideLabel
          type="email"
          autoComplete="email"
          onChange={onFieldChanged}
          onBlur={onFieldBlurred}
          pattern={EMAIL_ADDRESS_PATTERN_REGEX}
          invalid={isEmailInvalid}
          disabled={isAuthing}
        />
        <DigitsButton
          $variant={email ? "primary" : "secondary-dark"}
          $fullWidth
          disabled={isAuthing || !isEmailValid || !email}
          type="submit"
          aria-label="Continue with Email"
          leftDecorator={<SvgMail01 />}
          label="Continue with Email"
        />
        <StyledOrText>Or</StyledOrText>
        <GoogleLogin
          onStart={setGoogleLoading}
          onSuccess={onGoogleSuccess}
          onFailure={onGoogleFailure}
          onCancel={unsetGoogleLoading}
          disabled={isAuthing}
          loading={isGoogleLoading}
          render={ContinueWithGoogle}
          variant={email ? "secondary-dark" : "primary"}
        />
      </Form>
    </>
  )
}
